import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Sidebar from "../../components/sidebar/SideBar";
import { postsService, WordPressPost } from "../../api/api.service";
import "./PostPage.css";

const PostPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [post, setPost] = useState<WordPressPost | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPost = async () => {
            if (!id) return;

            try {
                setLoading(true);
                const data = await postsService.getPost(id);
                setPost(data);
            } catch (err) {
                setError("Failed to load the post. It may have been removed or is unavailable.");
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
        // Scroll to top on id change
        window.scrollTo(0, 0);
    }, [id]);

    if (loading) {
        return (
            <div className="post-page-loading">
                <Header variant="dark" />
                <div className="container loading-container">Loading post...</div>
                <Footer />
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="post-page-error">
                <Header variant="dark" />
                <div className="container error-container">
                    <h2>Error</h2>
                    <p>{error || "Post not found"}</p>
                    <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
                </div>
                <Footer />
            </div>
        );
    }

    const featureMedia = post._embedded?.["wp:featuredmedia"]?.[0];
    const authorData = post._embedded?.author?.[0];
    const imageUrl = postsService.getMediaBaseUrl(featureMedia);
    const authorName = authorData?.name || "Unknown Author";

    return (
        <>
            <Header variant="dark" />
            <div className="post-page-wrapper">
                <div className="post-layout">

                    {/* Main Content Article */}
                    <article className="post-content-area">
                        <header className="post-header">
                            <div className="post-meta-top">
                                <span className="post-date">{postsService.formatDate(post.date)}</span>
                                <span className="post-divider">|</span>
                                <span className="post-author">By {authorName}</span>
                            </div>
                            <h1 className="post-title" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                        </header>

                        {imageUrl && (
                            <div className="post-featured-image">
                                <img src={imageUrl} alt={post.title.rendered} />
                            </div>
                        )}

                        <div
                            className="post-body-content"
                            dangerouslySetInnerHTML={{ __html: post.content.rendered }}
                        />

                        {/* Tags & Categories Section */}
                        {post._embedded?.['wp:term'] && post._embedded['wp:term'].flat().length > 0 && (
                            <div className="post-tags-container">
                                <span className="tags-label">Tags:</span>
                                <div className="post-tags-list">
                                    {post._embedded['wp:term'].flat()
                                        .filter(term => term.taxonomy === 'post_tag' || term.taxonomy === 'category')
                                        .map(term => (
                                            <span key={term.id} className="post-tag-item">
                                                #{term.name}
                                            </span>
                                        ))}
                                </div>
                            </div>
                        )}

                        <div className="post-footer-nav">
                            <Link to="/blog" className="back-link">← Back to Blog</Link>
                        </div>
                    </article>

                    {/* Sidebar */}
                    <aside className="post-sidebar-area">
                        <Sidebar />
                    </aside>

                </div>
            </div>
            <Footer />
        </>
    );
};

export default PostPage;
